"use client";

import { type ReactNode, createContext, useRef, useContext, useEffect } from "react";
import { UserWorkspace } from "@/types";
import { createWorkspaceStore, defaultInitState, WorkspaceStore } from "@/stores/workspaceStore";
import { useStore } from "zustand";

export type WorkspaceStoreApi = ReturnType<typeof createWorkspaceStore>;

export const WorkspaceStoreContext = createContext<WorkspaceStoreApi | undefined>(undefined);

export interface WorkspaceStoreProviderProps {
  children: ReactNode;
  initialData?: {
    current: UserWorkspace;
    additional: UserWorkspace[];
  };
}

export const WorkspaceStoreProvider = ({ children, initialData }: WorkspaceStoreProviderProps) => {
  const storeRef = useRef<WorkspaceStoreApi>(null);
  if (!storeRef.current) {
    storeRef.current = createWorkspaceStore(initialData ?? defaultInitState);
  }

  // used for cross tab updates
  useEffect(() => {
    const handler = () => {
      // refetch the workspace data
      window.location.reload();
    };

    const storageListener = (event: StorageEvent) => {
      if (event.key === "workspace-update") {
        handler();
      }
    };

    window.addEventListener("storage", storageListener);

    return () => {
      window.removeEventListener("storage", storageListener);
    };
  }, []);

  return (
    <WorkspaceStoreContext.Provider value={storeRef.current}>
      {children}
    </WorkspaceStoreContext.Provider>
  );
};

export const useWorkspaceStore = <T,>(selector: (store: WorkspaceStore) => T): T => {
  const workspaceStoreContext = useContext(WorkspaceStoreContext);

  if (!workspaceStoreContext) {
    throw new Error(`useWorkspaceStore must be used within WorkspaceStoreProvider`);
  }

  return useStore(workspaceStoreContext, selector);
};
